import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, Download, Search, Trash2, FileText, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PickTicketSummary() {
  const [parsedData, setParsedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    project_number: '',
    program_number: '',
    set_number: '',
    sales_order_number: '',
  });

  const formComplete = form.project_number && form.program_number &&
    form.set_number && form.sales_order_number;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    setPdfFile(file);
    setLoading(true);
    setParsedData(null);

    const fd = new FormData();
    fd.append('file', file);

    try {
      const res = await axios.post(`${API}/api/warehouse/parse-pdf`, fd);
      if (res.data.error) {
        toast.error(`Parse error: ${res.data.error}`);
      } else {
        setParsedData(res.data);
        toast.success(`Parsed ${res.data.total_pages} pages — ${res.data.total_unique_items} unique items found`);
      }
    } catch (err) {
      toast.error('Failed to parse PDF');
    }
    setLoading(false);
  };

  const handleExport = async (format) => {
    if (!pdfFile || !formComplete) {
      toast.error('Please fill all required fields before exporting');
      return;
    }
    setExporting(true);

    const fd = new FormData();
    fd.append('file', pdfFile);

    const params = new URLSearchParams(form).toString();
    const endpoint = format === 'pdf' ? 'export-pdf' : 'export-excel';

    try {
      const res = await axios.post(`${API}/api/warehouse/${endpoint}?${params}`, fd, {
        responseType: 'blob',
      });

      const contentDisposition = res.headers['content-disposition'];
      let filename = `export.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported: ${filename}`);
    } catch (err) {
      toast.error('Export failed');
    }
    setExporting(false);
  };

  const handleReset = () => {
    setParsedData(null);
    setPdfFile(null);
    setSearch('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const filteredItems = parsedData?.items?.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return item.item_code.toLowerCase().includes(s) ||
      item.description.toLowerCase().includes(s) ||
      item.bin_locations.toLowerCase().includes(s);
  }) || [];

  return (
    <div className="space-y-5">
      {!parsedData ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-20 h-20 bg-amber-50 rounded-2xl flex items-center justify-center mb-6">
            <FileText className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Pick Ticket Summary</h2>
          <p className="text-sm text-slate-500 mb-8 text-center max-w-md">
            Upload a Sage Picking Sheet PDF. All pages will be parsed and items aggregated by item code.
          </p>
          <label className="cursor-pointer flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors font-semibold text-sm shadow-lg shadow-amber-200"
            data-testid="warehouse-upload-btn">
            <Upload className="w-4 h-4" />
            {loading ? 'Parsing...' : 'Upload PDF'}
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={handleUpload} disabled={loading} />
          </label>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Parsed Stats */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
              {parsedData.total_pages} pages · {parsedData.total_unique_items} items
            </span>
            <button onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              data-testid="warehouse-reset-btn">
              <Trash2 className="w-3.5 h-3.5" /> Reset
            </button>
          </div>

          {/* Form Fields */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-amber-600" />
              Export Information
              <span className="text-[10px] text-red-500 font-normal">(All fields required for export)</span>
            </h3>
            <div className="grid grid-cols-4 gap-3">
              {[
                { key: 'project_number', label: 'Project Number', placeholder: 'e.g. SR-3084' },
                { key: 'program_number', label: 'Program Number', placeholder: 'e.g. PT-3084' },
                { key: 'set_number', label: 'Set Number', placeholder: 'e.g. 15' },
                { key: 'sales_order_number', label: 'Sales Order No.', placeholder: 'e.g. SO-12345' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-[11px] font-semibold text-slate-600 block mb-1">{f.label}</label>
                  <input
                    value={form[f.key]}
                    onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-300 focus:border-amber-400 outline-none"
                    data-testid={`warehouse-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Search + Export */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by code, description, or bin location..."
                className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-300 focus:border-amber-400 outline-none"
                data-testid="warehouse-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => handleExport('excel')} disabled={!formComplete || exporting}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors shadow-sm ${
                  formComplete ? 'bg-green-600 text-white hover:bg-green-700 shadow-green-200' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`} data-testid="warehouse-export-excel-btn">
                <FileSpreadsheet className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Excel'}
              </button>
              <button onClick={() => handleExport('pdf')} disabled={!formComplete || exporting}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors shadow-sm ${
                  formComplete ? 'bg-red-600 text-white hover:bg-red-700 shadow-red-200' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`} data-testid="warehouse-export-pdf-btn">
                <FileText className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'PDF'}
              </button>
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="warehouse-items-table">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="px-3 py-2.5 text-left font-semibold text-xs w-12">#</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-xs w-36">Item Code</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-xs">Description</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-xs" style={{minWidth:'280px'}}>Bin Location</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-xs w-16">UOM</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-xs w-28">Required</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item, idx) => (
                    <tr key={item.item_code}
                      className={`border-b border-slate-100 hover:bg-amber-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="px-3 py-2 text-slate-400 text-xs">{idx + 1}</td>
                      <td className="px-3 py-2 font-mono font-semibold text-slate-800 text-xs" data-testid={`item-code-${item.item_code}`}>
                        {item.item_code}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{item.description}</td>
                      <td className="px-3 py-2">
                        {item.bin_locations ? (
                          <div className="text-[10px] text-blue-700 font-medium leading-relaxed max-h-12 overflow-y-auto" title={item.bin_locations}>
                            {item.bin_locations}
                          </div>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-slate-500 text-xs">{item.uom}</td>
                      <td className="px-3 py-2 text-center font-bold text-slate-800">{item.total_required}</td>
                    </tr>
                  ))}
                  {filteredItems.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                        {search ? 'No items match your search' : 'No items found'}
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredItems.length > 0 && (
                  <tfoot>
                    {(() => {
                      const byUnit = {};
                      filteredItems.forEach(i => {
                        const u = i.uom || 'EACH';
                        if (!byUnit[u]) byUnit[u] = { total: 0, count: 0 };
                        byUnit[u].total += i.total_required;
                        byUnit[u].count++;
                      });
                      const units = Object.keys(byUnit).sort();
                      return units.map((u, idx) => (
                        <tr key={u} className={idx === 0 ? "bg-slate-100 border-t-2 border-slate-300" : "bg-slate-50 border-t border-slate-200"}>
                          <td colSpan={2} className="px-3 py-2 font-bold text-slate-700">{idx === 0 ? 'TOTAL' : ''}</td>
                          <td className="px-3 py-2 font-semibold text-slate-600">{byUnit[u].count} items</td>
                          <td className="px-3 py-2 text-center font-semibold text-slate-500">{u}</td>
                          <td></td>
                          <td className="px-3 py-2 text-center font-bold text-slate-800">{Number(byUnit[u].total.toFixed(1))}</td>
                        </tr>
                      ));
                    })()}
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
